import { useState } from "react";
import { ammanStamp } from "../lib/dates";
import type { SyncStatus } from "../lib/api";
import {
  DEMO_INTEGRATIONS,
  DEMO_SETTINGS,
} from "../lib/settings-data";
import { DEMO_MODE } from "../lib/demo-data";
import { SendTestBox } from "./SendTestBox";
import {
  SecondaryButton, SectionHeader, StatusBadge, Surface, Tag,
} from "./primitives";

type Tab = "overview" | "integrations" | "sync" | "preferences";

type Integration = {
  id: string;
  name: string;
  description: string;
  status: "connected" | "pending" | "planned";
  detail: string;
  last_checked: string | null;
};

type HealthInfo = {
  ok?: boolean; demo?: boolean; supabase?: boolean; clevertap?: boolean; email?: boolean; email_from?: string;
  delivery?: { ready: boolean; missing: string[]; from: string };
} | undefined;

/**
 * On the live site, only what the Worker can actually check — a static list
 * here once said "Supabase: pending — using demo fixtures" beside a green
 * "Production · Live" banner. The demo build keeps its sample cards.
 */
function liveIntegrations(health: HealthInfo): Integration[] {
  if (DEMO_MODE) return DEMO_INTEGRATIONS;
  const now = new Date().toISOString();
  const card = (id: string, name: string, description: string, ok: boolean | undefined, yes: string, no: string): Integration => ({
    id, name, description, status: ok ? "connected" : "pending", detail: ok ? yes : no, last_checked: ok ? now : null,
  });
  return [
    card("supabase", "Database", "Subscribers, newsletters and sends (Supabase)", health?.supabase,
      "Connected", "Not connected: the Worker has no database credentials"),
    card("clevertap", "CleverTap", "Member profiles and events", health?.clevertap,
      "Configured", "Not configured on the Worker"),
    card("cf-email", "Test emails", "Sends a test to your own inbox (Cloudflare Email)", health?.email,
      `Sending from ${health?.email_from ?? "newsletter@altibbi.me"}`, "Not connected"),
    card("ses", "Sending to subscribers", "Broadcasts and automations (Amazon SES)", health?.delivery?.ready,
      `Sending from ${health?.delivery?.from ?? ""}`,
      `Still needs ${(health?.delivery?.missing ?? ["setup"]).join(", ")}`),
  ];
}

function integrationTone(status: string): "success" | "info" | "warn" | "muted" {
  if (status === "connected") return "success";
  if (status === "pending") return "warn";
  if (status === "planned") return "muted";
  return "info";
}


export function SettingsWorkspace({
  health, sync,
}: {
  health: HealthInfo;
  sync: SyncStatus | null | undefined;
}) {
  const [tab, setTab] = useState<Tab>("overview");
  const [notifyFail, setNotifyFail] = useState(DEMO_SETTINGS.notify_on_sync_fail);
  const [notifySent, setNotifySent] = useState(DEMO_SETTINGS.notify_on_campaign_sent);

  const integrations = liveIntegrations(health);
  const connected = integrations.filter((i) => i.status === "connected").length;
  const pending = integrations.filter((i) => i.status === "pending").length;

  return (
    <div style={{ display: "grid", gap: "var(--ui-space-4)" }}>
      {/* Environment banner */}
      <div className={`nl-env-banner${health?.demo !== false ? " demo" : ""}`}>
        <div>
          <div className="nl-env-title">
            {health?.demo !== false ? "Demo environment" : "Production environment"}
          </div>
          <div className="nl-env-body">
            {health?.demo !== false
              ? "Sample data only — CleverTap and Supabase credentials not required. Changes are not persisted."
              : "Live integrations active — sync writes to Supabase warehouse."}
          </div>
        </div>
        <StatusBadge tone={health?.demo !== false ? "info" : "success"}>
          {health?.demo !== false ? "Demo fixtures" : "Live"}
        </StatusBadge>
      </div>

      {/* Stats */}
      <Surface style={{ overflow: "hidden" }}>
        <div className="nl-stat-row">
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">Integrations</div>
            <div className="nl-stat-mini-value tabnum">{connected}/{integrations.length}</div>
          </div>
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">Pending setup</div>
            <div className="nl-stat-mini-value tabnum" style={pending ? { color: "var(--c-warn-text)" } : undefined}>
              {pending}
            </div>
          </div>
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">Last sync</div>
            <div className="nl-stat-mini-value" style={{ fontSize: 14, fontWeight: 600 }}>
              {sync?.last_success ? ammanStamp(sync.last_success).split(" ")[0] : "—"}
            </div>
          </div>
          <div className="nl-stat-mini">
            <div className="nl-stat-mini-label">Rows in warehouse</div>
            <div className="nl-stat-mini-value tabnum">
              {sync?.rows_loaded?.toLocaleString() ?? "—"}
            </div>
          </div>
        </div>
      </Surface>

      {/* Tab nav */}
      <Surface style={{ overflow: "hidden" }}>
        <div className="nl-settings-tabs" role="tablist" aria-label="Settings sections">
          {([
            ["overview", "Overview"],
            ["integrations", "Integrations"],
            ["sync", "Sync"],
            ["preferences", "Preferences"],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={`nl-settings-tab${tab === id ? " active" : ""}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>

        <div role="tabpanel" style={{ padding: "var(--ui-space-4)" }}>
          {tab === "overview" && <OverviewPanel health={health} sync={sync} />}
          {tab === "integrations" && <IntegrationsPanel health={health} />}
          {tab === "sync" && <SyncPanel sync={sync} />}
          {tab === "preferences" && (
            <PreferencesPanel
              notifyFail={notifyFail}
              notifySent={notifySent}
              onNotifyFail={setNotifyFail}
              onNotifySent={setNotifySent}
            />
          )}
        </div>
      </Surface>
    </div>
  );
}

function OverviewPanel({
  health, sync,
}: {
  health: HealthInfo;
  sync: SyncStatus | null | undefined;
}) {
  const integrations = liveIntegrations(health);
  return (
    <div style={{ display: "grid", gap: "var(--ui-space-4)" }}>
      <SectionHeader title="System health" meta="Quick status of all connected services" />
      <div className="nl-integration-grid">
        {integrations.map((i) => (
          <IntegrationCard key={i.id} integration={i} compact />
        ))}
      </div>

      {sync && (
        <div className="nl-settings-kv">
          <KV label="Last successful sync" value={sync.last_success ? ammanStamp(sync.last_success) : "—"} />
          <KV label="Source" value={sync.source} />
        </div>
      )}
    </div>
  );
}

function IntegrationsPanel({ health }: { health: HealthInfo }) {
  const integrations = liveIntegrations(health);
  return (
    <div style={{ display: "grid", gap: "var(--ui-space-4)" }}>
      <SectionHeader title="Connected services" meta="Configure credentials and test connections" />
      <div className="nl-integration-grid">
        {integrations.map((i) => (
          <IntegrationCard key={i.id} integration={i} />
        ))}
      </div>
      <SectionHeader title="Email Service" meta="Transactional previews from Cloudflare. Not bulk campaign sending." />
      <SendTestBox prominent />
    </div>
  );
}

function IntegrationCard({
  integration,
  compact = false,
}: {
  integration: Integration;
  compact?: boolean;
}) {
  return (
    <div className="nl-integration-card">
      <div style={{ display: "flex", gap: "var(--ui-space-3)", alignItems: "flex-start" }}>
        <div className="nl-integration-icon" aria-hidden style={{ marginBottom: 0, flexShrink: 0 }}>
          {integration.id === "clevertap" ? "CT" : integration.id === "supabase" ? "SB" : integration.id === "worker" ? "CF" : integration.id === "cf-email" ? "EM" : integration.id === "ses" ? "ES" : "R2"}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ fontWeight: 600, color: "var(--c-t1)" }}>{integration.name}</div>
            <StatusBadge tone={integrationTone(integration.status)}>{integration.status}</StatusBadge>
          </div>
          {!compact && (
            <p style={{ fontSize: "var(--ui-text-control)", color: "var(--c-t3)", margin: "4px 0 0", lineHeight: 1.45 }}>
              {integration.description}
            </p>
          )}
          <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t4)", marginTop: 6 }}>
            {integration.detail}
          </div>
          {integration.last_checked && (
            <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t4)", marginTop: 2 }} className="tabnum">
              Checked {ammanStamp(integration.last_checked)}
            </div>
          )}
        </div>
      </div>
          {!compact && integration.status !== "planned" && integration.id !== "cf-email" && (
        <div className="nl-action-row" style={{ marginTop: "var(--ui-space-3)", paddingLeft: 48 }}>
          <SecondaryButton disabled title="Demo — configure in Phase 2">Configure</SecondaryButton>
          <SecondaryButton disabled title="Demo">Test connection</SecondaryButton>
        </div>
      )}
    </div>
  );
}

function SyncPanel({ sync }: { sync: SyncStatus | null | undefined }) {
  return (
    <div style={{ display: "grid", gap: "var(--ui-space-4)" }}>
      <SectionHeader title="CleverTap subscriber sync" meta="Daily profile export into Supabase warehouse" />

      {DEMO_MODE && (
      <div className="nl-sync-schedule">
        <div>
          <div className="nl-stat-mini-label">Schedule</div>
          <div style={{ fontSize: "var(--ui-text-section)", fontWeight: 600, color: "var(--c-t1)", marginTop: 4 }}>
            {DEMO_SETTINGS.sync_schedule}
          </div>
        </div>
      </div>
      )}

      {sync && (
        <div className="nl-settings-kv">
          <KV label="Last run" value={sync.last_run_at ? ammanStamp(sync.last_run_at) : "—"} />
          <KV label="Last success" value={sync.last_success ? ammanStamp(sync.last_success) : "—"} />
          <KV label="Rows loaded" value={sync.rows_loaded?.toLocaleString() ?? "—"} />
          <KV label="Last error" value={sync.error ?? "None"} highlight={!!sync.error} />
        </div>
      )}

    </div>
  );
}

function PreferencesPanel({
  notifyFail, notifySent, onNotifyFail, onNotifySent,
}: {
  notifyFail: boolean;
  notifySent: boolean;
  onNotifyFail: (v: boolean) => void;
  onNotifySent: (v: boolean) => void;
}) {
  return (
    <div style={{ display: "grid", gap: "var(--ui-space-4)" }}>
      <SectionHeader title="Notifications" meta="Email alerts for sync and campaign events" />
      <ToggleRow
        label="Sync failure alerts"
        description="Email the team when a CleverTap sync fails or returns partial data"
        checked={notifyFail}
        onChange={onNotifyFail}
      />
      <ToggleRow
        label="Campaign sent confirmation"
        description="Notify editors when a scheduled campaign completes sending"
        checked={notifySent}
        onChange={onNotifySent}
      />

      <SectionHeader title="Defaults" meta="Applied to new newsletters and campaigns" />
      <div className="nl-settings-kv">
        <KV label="Sender name" value={DEMO_SETTINGS.default_sender_name} />
        <KV label="Reply-to" value={DEMO_SETTINGS.default_reply_to} />
        <KV label="Timezone" value={DEMO_SETTINGS.timezone} />
      </div>

      <SectionHeader title="Appearance" meta="Theme is controlled from the sidebar" />
      <div style={{ fontSize: "var(--ui-text-control)", color: "var(--c-t3)" }}>
        Use the <Tag>Dark</Tag> / <Tag>Light</Tag> toggle in the sidebar footer to switch themes.
      </div>
    </div>
  );
}

function KV({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="nl-kv-row">
      <span className="nl-kv-label">{label}</span>
      <span className={`nl-kv-value tabnum${highlight ? " warn" : ""}`}>{value}</span>
    </div>
  );
}

function ToggleRow({
  label, description, checked, onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="nl-toggle-row">
      <div>
        <div style={{ fontWeight: 500, color: "var(--c-t1)", fontSize: "var(--ui-text-control)" }}>{label}</div>
        <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)", marginTop: 2 }}>{description}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={`nl-toggle${checked ? " on" : ""}`}
        onClick={() => onChange(!checked)}
      >
        <span className="nl-toggle-knob" />
      </button>
    </div>
  );
}
